/**
 * Dental Stage 2: Discharge Information — interactive form for populating fields inline.
 */
import { Plus, X } from 'lucide-react'

import { SNOOTS_BG } from '@snootsvet/shared'

import { DatePicker } from '@/frontend/components/date-picker'
import { Button } from '@/frontend/components/ui/button'
import { Input } from '@/frontend/components/ui/input'
import { ProductSearch } from '@/frontend/features/inventory/product-search'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  type DentalDischargeStage2Fields,
  DentalDischargeStage2Pdf,
  defaultDentalDischargeStage2Fields,
  FEEDING_EXTRAS,
  FOLLOW_UP_TEXT,
  feedingText,
  procedureText,
  restText,
  SURGICAL_CARE_EXTRA,
  surgicalCareText,
  teethRemovedText,
} from '@/frontend/features/printing/documents/dental-discharge-stage2.print'
import {
  dentalClosingText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  OUT_OF_HOURS,
  THINGS_TO_NOTICE,
} from '@/frontend/features/printing/documents/discharge/shared'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function DentalDischargeStage2Form({
  context,
  value,
  onChange,
}: DocumentFormProps<DentalDischargeStage2Fields>) {
  const { patient, clinic } = context
  const name = patient.name

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 font-semibold">Dental Stage 2: Discharge Information</h1>

      <p>
        <span className="font-semibold">Procedure: </span>
        {procedureText(name)}
      </p>

      <p className="mt-3 flex flex-wrap items-center gap-1">
        <span>We have removed</span>
        <Input
          className="inline-flex w-48"
          placeholder="number/description"
          value={value.teethRemoved}
          onChange={(e) => onChange({ ...value, teethRemoved: e.target.value })}
        />
        <span>teeth.</span> {teethRemovedText(name)}
      </p>

      <h2 className="text-headline-md">Rest and activity:</h2>
      <p className="mt-1">{restText(name)}</p>

      <h2 className="text-headline-md">Feeding:</h2>
      <p className="mt-1">{feedingText(name)}</p>
      {FEEDING_EXTRAS.map((item) => (
        <p key={item} className="mt-1">
          {item}
        </p>
      ))}

      <h2 className="text-headline-md">Medication:</h2>
      <p className="mt-1">{medicationText(name)}</p>
      <p className="mt-2">Medication to go home with are as follows;</p>
      <div className="mt-2 space-y-2">
        {value.medications.map((med, i) => (
          <div key={med.key} className="flex gap-2 rounded border p-2">
            <div className="flex-1 space-y-2">
              <ProductSearch
                onChange={(p) => {
                  const updated = [...value.medications]
                  updated[i] = { ...updated[i], productId: p?.id ?? '', name: p?.name ?? '' }
                  onChange({ ...value, medications: updated })
                }}
                type={['medication']}
                placeholder="Search medication..."
                showClear
              />
              <Input
                placeholder="Instructions (e.g. 1 tablet twice daily with food)"
                value={med.instructions}
                onChange={(e) => {
                  const updated = [...value.medications]
                  updated[i] = { ...updated[i], instructions: e.target.value }
                  onChange({ ...value, medications: updated })
                }}
              />
            </div>
            <Button
              variant="ghost"
              className="self-start"
              onClick={() => {
                const updated = value.medications.filter((_, j) => j !== i)
                onChange({ ...value, medications: updated })
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() =>
            onChange({
              ...value,
              medications: [
                ...value.medications,
                { key: crypto.randomUUID(), productId: '', name: '', instructions: '' },
              ],
            })
          }
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add medication
        </Button>
      </div>
      <p className="mt-2">{MEDICATION_SIDE_EFFECTS}</p>

      <h2 className="text-headline-md">Surgical site care:</h2>
      <p className="mt-1">{surgicalCareText(name)}</p>
      <p className="mt-1">{SURGICAL_CARE_EXTRA}</p>

      <h2 className="text-headline-md">Follow up appointments:</h2>
      <p className="mt-1">{FOLLOW_UP_TEXT}</p>
      <p className="mt-2 font-semibold">Follow up appointment dates:</p>
      <div className="mt-2 flex items-center gap-2">
        <DatePicker
          value={value.followUpDate1 ?? undefined}
          onChange={(d) => onChange({ ...value, followUpDate1: d ?? null })}
          placeholder="Date 1"
          className="w-48"
        />
        <span>&</span>
        <DatePicker
          value={value.followUpDate2 ?? undefined}
          onChange={(d) => onChange({ ...value, followUpDate2: d ?? null })}
          placeholder="Date 2"
          className="w-48"
        />
      </div>

      <h2 className="text-headline-md">Other things you may notice:</h2>
      <ul className="mt-1 list-disc pl-6">
        {THINGS_TO_NOTICE.map((item) => (
          <li key={item.slice(0, 30)} className="mt-1">
            {item}
          </li>
        ))}
      </ul>

      <h2 className="text-headline-md">Out of hours contact:</h2>
      <p className="mt-1">{OUT_OF_HOURS}</p>

      <p className="mt-4">{dentalClosingText(name)}</p>
      <p className="mt-2">Snoots Vet, North London.</p>
    </DocumentPreview>
  )
}

export const dentalDischargeStage2 = withDocumentEditor({
  id: 'dental-discharge-stage2',
  label: 'Dental Discharge — Stage 2',
  // Dental procedures are carried out at the Bounds Green clinic.
  clinic: SNOOTS_BG,
  defaultFields: defaultDentalDischargeStage2Fields,
  Preview: DentalDischargeStage2Form,
  Pdf: DentalDischargeStage2Pdf,
})
